// src/pages/Tutorial.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, ClipboardList, FileText, Receipt, Wrench, RefreshCw, ExternalLink, ShieldAlert
} from 'lucide-react';

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="bg-white dark:bg-gray-800 rounded-2xl border dark:border-gray-700 shadow mb-8">
    <header className="flex items-center gap-3 px-5 py-4 border-b dark:border-gray-700">
      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
        <Icon className="w-5 h-5 text-blue-700 dark:text-blue-300" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
    </header>
    <div className="p-5">{children}</div>
  </section>
);

const Step = ({ n, title, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold shrink-0">{n}</div>
    <div>
      <div className="font-semibold text-gray-900 dark:text-gray-100">{title}</div>
      <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{children}</div>
    </div>
  </div>
);

const Callout = ({ tone='info', children }) => {
  const styles = {
    info:    'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-200',
    warn:    'bg-yellow-50 border-yellow-200 text-yellow-900 dark:bg-yellow-950/40 dark:border-yellow-900 dark:text-yellow-100',
    danger:  'bg-red-50 border-red-200 text-red-900 dark:bg-red-950/40 dark:border-red-900 dark:text-red-200',
    success: 'bg-green-50 border-green-200 text-green-900 dark:bg-green-950/40 dark:border-green-900 dark:text-green-200',
  }[tone] || 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-200';

  return (
    <div className={`mt-3 rounded-xl border px-3 py-2 text-sm flex items-start gap-2 ${styles}`}>
      {tone === 'warn' || tone === 'danger'
        ? <ShieldAlert className="mt-0.5 w-4 h-4" />
        : <BookOpen className="mt-0.5 w-4 h-4" />}
      <div className="flex-1">{children}</div>
    </div>
  );
};

/* =================== Banner compacto sempre visível =================== */
function PrereqNotice() {
  return (
    <div className="mb-4">
      <div className="flex items-start sm:items-center justify-between gap-3 rounded-xl border border-red-200 dark:border-red-800 bg-red-50/60 dark:bg-red-950/30 px-3 py-2">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 w-4 h-4 text-red-700 dark:text-red-300 shrink-0" />
          <div className="text-sm leading-snug text-red-800 dark:text-red-100">
            <b>Atenção:</b> para emitir <b>NF-e</b> ou <b>NFS-e</b> é obrigatório ter
            <b> ao menos 1 Cliente</b> e <b>ao menos 1 Produto/Serviço</b> cadastrados.
            <span className="ml-1 text-red-700/80 dark:text-red-200/90">Evita erros na autorização e cálculos fiscais.</span>
          </div>
        </div>

        {/* Links curtinhos, opcionais e discretos */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/clientes"
            className="text-xs font-medium text-red-700 dark:text-red-200 underline decoration-red-300 hover:decoration-red-500"
          >
            Cadastrar cliente
          </Link>
          <span className="text-red-300 dark:text-red-700 select-none">•</span>
          <Link
            to="/produtos"
            className="text-xs font-medium text-red-700 dark:text-red-200 underline decoration-red-300 hover:decoration-red-500"
          >
            Cadastrar produto/serviço
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Tutorial() {
  return (
    <div className="flex h-full relative">
      <main className="flex-grow w-full max-w-[1200px] mx-auto p-6">
        {/* Título */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-300" />
            Guia Rápido — OS & Notas Fiscais
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Este tutorial mostra dois jeitos de emitir notas: <b>usando uma OS</b> (recomendado quando há controle do serviço/peças)
            e <b>sem OS</b> pela tela de <i>Notas Fiscais</i> (emissão avulsa).
          </p>
        </div>

        {/* Banner compacto (sempre visível) */}
        <PrereqNotice />

        {/* Índice */}
        <nav className="bg-white dark:bg-gray-800 rounded-2xl border dark:border-gray-700 shadow p-4 mb-8">
          <div className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 mb-2">Navegue por seção</div>
          <ul className="grid sm:grid-cols-2 gap-2 text-sm">
            <li><a href="#abrir-os" className="underline hover:no-underline">Abrir uma OS</a></li>
            <li><a href="#fluxo-os" className="underline hover:no-underline">Fluxo da OS (status)</a></li>
            <li><a href="#emitir-com-os" className="underline hover:no-underline">Emitir notas a partir da OS</a></li>
            <li><a href="#consultar-cancelar" className="underline hover:no-underline">Consultar / Ver / Cancelar</a></li>
            <li><a href="#emitir-avulsa" className="underline hover:no-underline">Emitir notas sem OS (avulsas)</a></li>
            <li><a href="#duvidas" className="underline hover:no-underline">Dúvidas e Problemas</a></li>
          </ul>
        </nav>

        {/* Abrir OS */}
        <Section id="abrir-os" icon={ClipboardList} title="Abrir uma OS (Ordem de Serviço)">
          <div className="space-y-4">
            <Step n={1} title={<>Acesse <Link to="/os" className="text-blue-700 dark:text-blue-300 underline">Ordens de Serviço</Link></>} >
              Na barra lateral (Operacional), clique em <b>Ordens de Serviço</b>.
            </Step>
            <Step n={2} title="Criar">
              Clique em <b>Nova OS</b>. Preencha <b>Cliente</b>, (opcional) <b>Veículo</b>, e adicione <b>Produtos</b> e/ou <b>Serviços</b>.
              Os totais são calculados automaticamente.
            </Step>
            <Step n={3} title="Salvar">
              Salve a OS. No card/tabela ela aparecerá com status <b>Aberta</b>.
            </Step>
            <Callout tone="info">
              Ao criar a OS, o estoque de produtos é descontado (conforme <code>descontarEstoqueProdutos</code> no seu código).
              Ao excluir a OS, o estoque é devolvido.
            </Callout>
          </div>
        </Section>

        {/* Fluxo OS */}
        <Section id="fluxo-os" icon={Wrench} title="Fluxo da OS (Iniciar → Finalizar)">
          <div className="space-y-4">
            <Step n={1} title="Iniciar atendimento">
              Na OS com status <b>Aberta</b>, clique em <b>Iniciar atendimento</b> (ícone ▶).
              O status muda para <b>Em andamento</b>.
            </Step>
            <Step n={2} title="Finalizar">
              Quando terminar, clique em <b>Finalizar OS</b> (✓). Só com status <b>Finalizada</b> você pode emitir notas.
            </Step>
            <Callout tone="warn">
              Se a OS não tiver <b>Produtos</b>, não será possível emitir <b>NF-e</b>.  
              Se não tiver <b>Serviços</b>, não será possível emitir <b>NFS-e</b>.
            </Callout>
          </div>
        </Section>

        {/* Emitir com OS */}
        <Section id="emitir-com-os" icon={Receipt} title="Emitir notas fiscais a partir da OS">
          <div className="space-y-4">
            <Step n={1} title="Abrir ações de nota">
              Localize a OS <b>Finalizada</b>. Nos cards/tabela, você verá os botões:
              <ul className="list-disc ml-5 mt-2">
                <li><b>Emitir NFS-e</b> (serviços) — ícone de nota <FileText className="inline w-4 h-4" /> azul.</li>
                <li><b>Emitir NF-e</b> (produtos) — ícone de nota <FileText className="inline w-4 h-4" /> verde.</li>
              </ul>
              Os botões têm tooltip com a ação (ex.: “Emitir NFS-e”, “Emitir novamente NF-e”).
            </Step>
            <Step n={2} title="Emitir">
              Clique em <b>Emitir NFS-e</b> ou <b>Emitir NF-e</b>.  
              O front chama seu backend com token do Firebase (vide <code>emitirNFSe</code> / <code>emitirNFe</code> em <code>OS.jsx</code>).
            </Step>
            <Step n={3} title="Acompanhar status">
              Enquanto a prefeitura/SEFAZ processa, o status aparecerá como <b>Aguardando autorização</b> / <b>Processando</b>.  
              Use <b>Atualizar status</b> (ícone <RefreshCw className="inline w-4 h-4" />) para consultar de novo.
            </Step>
            <Callout tone="success">
              Ao autorizar, a OS guarda links úteis: <b>DANFe</b> e <b>XML</b> (NF-e) e o <b>Viewer</b> (NFS-e).
              Você pode ver tudo no botão <b>Notas Fiscais</b> do card de OS ou “Ver detalhes e cancelar” na tabela.
            </Callout>
          </div>
        </Section>

        {/* Consultar/Cancelar/Ver */}
        <Section id="consultar-cancelar" icon={ExternalLink} title="Consultar, visualizar e cancelar">
          <div className="space-y-4">
            <Step n={1} title="Visualizar documentos">
              Abra o modal <b>Notas Fiscais</b> da OS. Lá você terá:
              <ul className="list-disc ml-5 mt-2">
                <li><b>NF-e</b>: links de <b>DANFe (PDF)</b> e <b>XML</b>.</li>
                <li><b>NFS-e</b>: link do <b>viewer</b> (via backend, mesmo quando a Focus retorna caminho relativo).</li>
              </ul>
            </Step>
            <Step n={2} title="Atualizar status">
              Se a nota estiver pendente/erro, clique em <b>Atualizar status</b> — isso persiste os novos dados na OS.
            </Step>
            <Step n={3} title="Cancelar">
              Pelo modal, clique em <b>Cancelar</b> (se permitido). Será solicitada uma justificativa e o backend é acionado.
            </Step>
            <Callout tone="danger">
              Emissão/cancelamento dependem dos endpoints do seu backend. Mensagens de rejeição da SEFAZ/Prefeitura são exibidas no modal.
            </Callout>
          </div>
        </Section>

        {/* Avulsa */}
        <Section id="emitir-avulsa" icon={FileText} title="Emitir notas sem OS (Notas Fiscais avulsas)">
          <div className="space-y-4">
            <Step n={1} title={<>Acesse <Link to="/notas" className="text-blue-700 dark:text-blue-300 underline">Notas Fiscais</Link></>}>
              Tela dedicada para emissão sem vínculo com OS.
            </Step>
            <Step n={2} title="Escolha o tipo">
              Selecione <b>NF-e (Produtos)</b> ou <b>NFS-e (Serviços)</b>.
            </Step>
            <Step n={3} title="Selecione o cliente e adicione itens">
              Busque o cliente, e adicione <b>Produtos</b> (NF-e) ou <b>Serviços</b> (NFS-e). Os totais são calculados.
            </Step>
            <Step n={4} title="Emitir">
              Clique em <b>Emitir NF-e</b> ou <b>Emitir NFS-e</b>. O documento é salvo em <code>empresas/&lt;id&gt;/notas</code> com numeração própria.
            </Step>
            <Step n={5} title="Acompanhar / Visualizar / Cancelar">
              A listagem abaixo mostra todas as notas avulsas:
              <ul className="list-disc ml-5 mt-2">
                <li><b>Atualizar status</b> (consulta no backend).</li>
                <li><b>Ver DANFe/XML</b> (NF-e) ou <b>abrir NFS-e</b> no viewer.</li>
                <li><b>Cancelar</b> — requer endpoints <code>cancelar-*-avulsa</code> no backend.</li>
              </ul>
            </Step>
            <Callout tone="info">
              Diferente da OS, a emissão avulsa <b>não mexe no estoque</b> (fluxo administrativo simples).
            </Callout>
          </div>
        </Section>

        {/* Dúvidas / Troubleshooting */}
        <Section id="duvidas" icon={RefreshCw} title="Dúvidas & Problemas comuns">
          <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
            <li>
              <b>Status fica “Aguardando autorização” por muito tempo</b>: use <i>Atualizar status</i>. Caso persista, verifique a situação do serviço na prefeitura/SEFAZ.
            </li>
            <li>
              <b>Erro/Rejeição</b>: o modal traz o texto da SEFAZ/Prefeitura (ex.: CFOP, CST/CSOSN, NCM, tributação). Ajuste os cadastros e reemita.
            </li>
            <li>
              <b>Sem link de DANFe/XML</b>: a página já tenta construir via backend (<code>/baixar-danfe</code>, <code>/baixar-xml</code>) a partir da chave. Garanta que a chave foi gravada.
            </li>
            <li>
              <b>Cancelamento avulso indisponível</b>: se o backend não tiver <code>/cancelar-nfe-avulsa</code> ou <code>/cancelar-nfse-avulsa</code>, o botão apresentará erro informativo.
            </li>
            <li>
              <b>Permissões</b>: confira as regras do Firestore e o token (Bearer) do Firebase nas chamadas do backend.
            </li>
          </ul>
        </Section>
      </main>
    </div>
  );
}
